import { z } from "zod";
import { patchOf } from "../common/patch.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
import { dataSubjectRequestStatusSchema, dataSubjectRequestTypeSchema, privacyTextKindSchema, } from "../privacy/enums.js";
/**
 * Sürücü (müşteri uygulaması) API'si — PLATFORM düzeyi, ayrı kimlik (ADR 0017 §2–3).
 *
 *  - Giriş telefonla: `POST /driver/auth/otp` 6 haneli tek kullanımlık kod gönderir (yalnızca ÖZETİ saklanır, 5 dk,
 *    en çok 5 deneme); yanıt telefonun kayıtlı olup olmadığını SÖYLEMEZ. `POST /driver/auth/verify` ilk girişte hesabı açar.
 *  - Erişim belirteci ayrı kitledir (`aud = station-driver`): personel uçları sürücü belirtecini, sürücü uçları personel
 *    belirtecini reddeder. Yenileme belirteci her kullanımda DÖNER (eskisi geçersizleşir; yeniden kullanımı tüm oturumları
 *    kapatır).
 *  - Programa bağlanma: işletmenin ürettiği davet belirteciyle (QR / SMS) + yürürlükteki aydınlatma ve bağlantı rızası
 *    metinlerinin kabulüyle. Bağ istenildiği an koparılır (= rızanın geri alınması).
 */
export const driverOtpRequestSchema = z.object({
    phone: z.string().trim().min(7).max(30).describe("KVKK: telefon (0532…, +90532… biçimleri kabul edilir)"),
});
export const driverOtpResponseSchema = z.object({
    sent: z.literal(true).describe("Telefonun kayıtlı olup olmadığından bağımsız olarak AYNI yanıt"),
    expiresInSeconds: z.number().int(),
    codeLength: z.number().int(),
});
export const driverVerifySchema = z.object({
    phone: z.string().trim().min(7).max(30),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, "Kod 6 haneli olmalıdır"),
    deviceName: z.string().trim().min(1).max(120).nullable().default(null),
});
export const DRIVER_STATUSES = ["ACTIVE", "DISABLED", "DELETED"];
export const DRIVER_LOCALES = ["tr", "en"];
export const driverProfileSchema = z.object({
    id: uuidSchema,
    phone: z.string().nullable().describe("KVKK: sürücünün kendi telefonu"),
    fullName: z.string().nullable(),
    email: z.string().nullable(),
    locale: z.enum(DRIVER_LOCALES),
    status: z.enum(DRIVER_STATUSES),
    phoneVerifiedAt: timestampSchema,
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const driverTokenPairSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresIn: z.number().int().describe("Saniye"),
    refreshToken: z.string().describe("Tek kullanımlık; her yenilemede yenisi döner"),
    refreshTokenExpiresAt: timestampSchema,
});
export const driverSessionSchema = driverTokenPairSchema.extend({
    driver: driverProfileSchema,
    isNewDriver: z.boolean(),
});
export const driverRefreshSchema = z.object({ refreshToken: z.string().trim().min(20).max(200) });
export const updateDriverProfileSchema = patchOf(z.object({
    fullName: z.string().trim().min(2).max(120).nullable().default(null),
    email: z.string().trim().min(3).max(254).nullable().default(null),
    locale: z.enum(DRIVER_LOCALES).default("tr"),
})).extend({ version: z.number().int().positive() });
// ── Programa bağlanma ───────────────────────────────────────────────────────
export const driverProgramBrandingSchema = z.object({
    name: z.string(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    logoUrl: z.string().nullable().describe("Kısa ömürlü imzalı adres"),
});
export const driverLinkPreviewQuerySchema = z.object({ token: z.string().trim().min(20).max(2000) });
export const driverLinkPreviewSchema = z.object({
    tenantName: z.string(),
    program: driverProgramBrandingSchema.nullable(),
    customer: z.object({
        maskedName: z.string(),
        loyaltyCardNoMasked: z.string().nullable(),
    }),
    texts: z
        .array(z.object({
        id: uuidSchema,
        kind: privacyTextKindSchema,
        purposeCode: z.string().nullable(),
        revision: z.number().int(),
        title: z.string(),
        body: z.string(),
        language: z.string(),
    }))
        .describe("Kabul edilmesi gereken yürürlükteki metinler: aydınlatma + DRIVER_APP_LINK açık rızası"),
    expiresAt: timestampSchema,
});
export const acceptDriverLinkSchema = z.object({
    token: z.string().trim().min(20).max(2000),
    acceptedTextIds: z.array(uuidSchema).min(1).max(10),
});
export const driverLinkSchema = z.object({
    id: uuidSchema,
    tenantId: uuidSchema,
    tenantName: z.string(),
    linkedAt: timestampSchema,
    program: driverProgramBrandingSchema.nullable(),
    loyaltyCardNo: z.string().nullable(),
    pointsBalance: z.string().nullable(),
    tierCode: z.string().nullable(),
    tierName: z.string().nullable(),
});
export const driverLinkListSchema = z.object({ items: z.array(driverLinkSchema) });
export const driverDataRequestSchema = z.object({
    requestType: dataSubjectRequestTypeSchema,
    description: z.string().trim().min(5).max(4000),
});
export const driverDataRequestResultSchema = z.object({
    requestId: uuidSchema,
    requestNo: z.string(),
    requestType: dataSubjectRequestTypeSchema,
    status: dataSubjectRequestStatusSchema,
    receivedAt: timestampSchema,
    dueAt: timestampSchema,
});
//# sourceMappingURL=drivers.js.map